import { useEffect, useState, type FocusEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  listPartnerCompaniesPublic,
  type PartnerCompanyOption,
} from '../api/screen2Api';

/**
 * Partner company type-ahead for signup modal Screen 2 (feature 076-02).
 *
 * Firm names are only discoverable by typing: no suggestions until 2+
 * characters, then server-side starts-with matches. "My company isn't
 * listed" stays reachable from every panel state so the unlisted free-text
 * branch is never blocked.
 */

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

type Props = {
  disabled: boolean;
  errorMessage: string | null;
  onSelectCompany: (company: PartnerCompanyOption) => void;
  onSelectUnlisted: () => void;
  onClearSelection: () => void;
};

export const PartnerCompanyTypeahead = ({
  disabled,
  errorMessage,
  onSelectCompany,
  onSelectUnlisted,
  onClearSelection,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const searchEnabled =
    open && selectedLabel === null && debouncedQuery.length >= MIN_QUERY_LENGTH;

  const companiesQuery = useQuery({
    queryKey: ['pre-login', 'partner-companies', 'public', debouncedQuery],
    queryFn: () => listPartnerCompaniesPublic(debouncedQuery),
    enabled: searchEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const handleChange = (value: string) => {
    setInputValue(value);
    setOpen(true);
    if (selectedLabel !== null) {
      setSelectedLabel(null);
      onClearSelection();
    }
  };

  const selectCompany = (company: PartnerCompanyOption) => {
    setSelectedLabel(company.name);
    setInputValue(company.name);
    setOpen(false);
    onSelectCompany(company);
  };

  const selectUnlisted = () => {
    const label = "My company isn't listed";
    setSelectedLabel(label);
    setInputValue(label);
    setOpen(false);
    onSelectUnlisted();
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }
    setOpen(false);
  };

  const showMinLengthHint =
    selectedLabel === null && debouncedQuery.length < MIN_QUERY_LENGTH;
  const showLoading = searchEnabled && companiesQuery.isFetching;
  const results = searchEnabled && !companiesQuery.isFetching ? companiesQuery.data : undefined;

  return (
    <div className="relative" onBlur={handleBlur}>
      <Label htmlFor="signup-company">Partner company *</Label>
      <Input
        id="signup-company"
        role="combobox"
        aria-expanded={open}
        aria-controls="signup-company-listbox"
        aria-autocomplete="list"
        autoComplete="off"
        placeholder="Start typing your company's name"
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        disabled={disabled}
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? 'signup-company-error' : undefined}
      />
      {errorMessage && (
        <p id="signup-company-error" className="mt-1.5 text-xs text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
      {open && (
        <div
          id="signup-company-listbox"
          role="listbox"
          aria-label="Partner company suggestions"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-md"
        >
          {showMinLengthHint && (
            <p className="px-3 py-2 text-xs text-gray-500">
              Type at least 2 characters to search for your company.
            </p>
          )}
          {showLoading && <p className="px-3 py-2 text-xs text-gray-500">Searching…</p>}
          {results?.map((company) => (
            <button
              key={company.id}
              type="button"
              role="option"
              aria-selected={false}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectCompany(company)}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
            >
              {company.name}
            </button>
          ))}
          {results?.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-500">No matching companies found.</p>
          )}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={selectUnlisted}
            className="block w-full border-t border-gray-100 px-3 py-2 text-left text-sm text-[#026857] hover:bg-gray-50"
          >
            My company isn&apos;t listed
          </button>
        </div>
      )}
    </div>
  );
};
